<template>
	<div class="opportunity" :class="{ compact, peeked: compact && !expanded }">
		<div ref="headerEl" class="header">
			<div class="header-top">
				<div v-if="isValidPrice(item.priceExpectation) || isValidPrice(item.price)" class="price-block">
					<span class="price-label">{{ $t("opportunities.askingPrice") }}</span>
					<span class="price-value">
						{{ formatCurrency(isValidPrice(item.priceExpectation) ? "priceExpectation" : "price") }}
					</span>
				</div>
				<h3 v-else class="title">
					{{
						item.type === "off_market_official"
							? $t("opportunities.offMarketPrefix") + " #" + item.hubspot_record_id
							: item.isPlaceholder
								? syncStatusLabel
								: item.address
					}}
				</h3>
				<div class="header-actions">
					<button
						v-if="!compact"
						type="button"
						class="icon-button collapse-btn"
						:title="$t('callToActions.collapsePanel')"
						@click="emit('collapse')"
					>
						<AppIcon :name="IconName.PanelRightCollapse" :size="18" :stroke-width="1.2" />
					</button>

					<button
						type="button"
						class="icon-button collapse-btn"
						:title="$t('callToActions.close')"
						@click="emit('close')"
					>
						<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
					</button>
				</div>
			</div>

			<h3 v-if="isValidPrice(item.priceExpectation) || isValidPrice(item.price)" class="title">
				{{
					item.type === "off_market_official"
						? $t("opportunities.offMarketPrefix") + " #" + item.hubspot_record_id
						: item.isPlaceholder
							? syncStatusLabel
							: item.address
				}}
			</h3>

			<div class="sub-title">{{ item.isPlaceholder ? syncStatusSubtitle : formatAddress(item) }}</div>

			<div class="badge-row">
				<div v-if="item.type" class="badge" :class="`badge--${item.type}`">
					{{ $t("opportunities-layers." + item.type) }}
				</div>
				<span v-for="tag in cardTags" :key="tag.key" class="chip" :class="`chip--${tag.key}`">
					{{ tag.label }}
				</span>
				<div class="detail-actions">
					<Button
						v-if="canEditOpportunity && showFullDetails"
						class="toggle-edit-btn"
						size="s"
						variant="primary"
						:disabled="isSavingOpportunity"
						@click="isEditMode = !isEditMode"
					>
						{{ isEditMode ? t("transaction.card.exitEditMode") : t("transaction.card.enterEditMode") }}
					</Button>
					<OpportunityEvaluation
						class="opportunity-evaluation"
						:opportunity-id="Number(item.id)"
						:initial-status="item.status"
						:project-id="currentProject?.id"
						:disabled="item.isPlaceholder"
						@interact="captureOpportunityInteraction"
					/>
				</div>
			</div>
		</div>

		<div class="separator"></div>

		<div class="items">
			<div v-if="item.superficy || item.constructible" class="stat-tiles">
				<div v-if="item.superficy" class="tile">
					<span class="label">{{ areaFieldLabel(OpportunityUnitField.LandArea) }}</span>
					<span class="value"
						>{{ formatAreaField("superficy") }} <span class="unit">{{ areaUnitSymbol }}</span></span
					>
				</div>
				<div v-if="item.constructible" class="tile">
					<span class="label">{{ areaFieldLabel(OpportunityUnitField.BuildableArea) }}</span>
					<span class="value"
						>{{ formatAreaField("constructible") }} <span class="unit">{{ areaUnitSymbol }}</span></span
					>
				</div>
			</div>

			<div v-if="hasRealImage && showFullDetails" class="hero-image">
				<img
					class="overview-image"
					:class="{ loaded: isHeroImageLoaded }"
					:src="item.image"
					alt=""
					@load="isHeroImageLoaded = true"
					@error="isHeroImageLoaded = true"
				/>
				<div v-if="!isHeroImageLoaded" class="image-skeleton" />
			</div>

			<div v-if="compact && !expanded && compactAssetClassification.length > 0" class="compact-asset-badges">
				<span v-for="badge in compactAssetClassification" :key="badge" class="compact-asset-badge">{{ badge }}</span>
			</div>

			<div v-if="isListingLink || showCreateTargetButton" class="opportunity-actions">
				<Button v-if="isListingLink" :href="item.link!" target="_blank" variant="external" size="m" class="listing-btn">
					{{ $t("callToActions.openDetailedOpportunity") }}
				</Button>

				<Button
					v-if="showCreateTargetButton"
					type="button"
					class="create-target-btn"
					@click="handleQuickOpportunity"
					:disabled="isQuickOpportunityLoading"
					variant="target"
					size="m"
				>
					<span v-if="isQuickOpportunityLoading" class="loader"></span>
					<span v-else>{{ $t("callToActions.createOpportunity") }}</span>
				</Button>
			</div>

			<div v-if="showFullDetails" class="details-section">
				<div v-if="item.description" class="description">
					<h4>{{ $t("opportunities.summary") }}</h4>
					<p>{{ item.description }}</p>
				</div>
			</div>

			<div
				v-if="item.type === 'opportunity_target' && canReadOpportunitySensitive && showFullDetails"
				class="accordions"
			>
				<AccordionRoot
					:multiple="true"
					:default-value="['targetTracking']"
					persistence-key="opportunity-card-v2-target-accordion"
				>
					<AccordionItem
						value="targetTracking"
						class="target-tracking-item"
						:title="$t('opportunities.accordions.targetTracking')"
					>
						<div class="content target-tracking-content">
							<div
								v-if="
									item.type === OpportunityType.OpportunityTarget &&
									canReadOpportunitySensitive &&
									config.ui.has(Feature.OpportunityStage)
								"
								class="target-dropdown"
							>
								<ArkListboxDropdown
									v-model="stageDropdown"
									:options="opportunityStageOptions"
									:multiple="false"
									:clearable="false"
									:label="$t('opportunities.stage')"
									:placeholder="$t('opportunities.stagePlaceholder')"
									:disabled="!canWriteOpportunitySensitive || isStageLoading || item.isPlaceholder"
									@change="captureOpportunitySelectInteraction('pipelineStage', $event)"
									@update:model-value="handleStageChange"
								/>
							</div>

							<div
								v-if="item.type === OpportunityType.OpportunityTarget && canReadOpportunitySensitive"
								class="target-dropdown"
							>
								<ArkListboxDropdown
									v-model="priorityDropdownValue"
									:options="opportunityPriorityOptions"
									:multiple="false"
									:clearable="false"
									:label="$t('opportunities.priority')"
									:placeholder="$t('opportunities.priorityPlaceholder')"
									:disabled="!canWriteOpportunitySensitive || isPriorityLoading || item.isPlaceholder"
									@change="captureOpportunitySelectInteraction('priority', $event)"
									@update:model-value="handlePriorityChange"
								/>
							</div>

							<div
								v-if="
									store.isUserLoggedIn &&
									store.currentUser.has(Permission.OpportunityTargetMove) &&
									config.ui.has(Feature.TransferOpportunityTarget)
								"
								class="target-actions"
							>
								<Button
									v-if="currentProject?.type === ProjectType.Workspace"
									type="button"
									variant="primary"
									size="m"
									class="ccv-btn"
									:disabled="isMakeCcvLoading || item.isSyncPending"
									@click="handleMakeCcvClick"
								>
									<span v-if="isMakeCcvLoading" class="loader"></span>
									<span v-else>{{ $t("opportunities.makeCcvButton") }}</span>
								</Button>

								<Button
									type="button"
									variant="secondary"
									size="m"
									class="transfer-btn"
									:disabled="isTransferLoading || item.isPlaceholder"
									@click="isTransferModalOpen = true"
								>
									<span v-if="isTransferLoading" class="loader"></span>
									<template v-else>
										<AppIcon :name="IconName.ArrowsSwapCompact" :size="16" :stroke-width="1.3" />
										{{ $t("opportunities.transferButton") }}
									</template>
								</Button>
							</div>

							<div class="opportunity-type">
								<p v-if="item?.type">
									<span v-if="item.sales_process_status === 'accepted_psa'"
										><b>{{ $t("opportunities.sales_process_status.accepted_offer") }}</b></span
									>
								</p>
							</div>

							<div
								v-if="
									store.isUserLoggedIn &&
									currentProject?.type === ProjectType.Workspace &&
									store.currentUser.has(Permission.OpportunityTargetWrite)
								"
								class="delete-target"
							>
								<Button
									variant="ghost"
									size="m"
									class="delete-btn"
									:disabled="isDeleteLoading"
									@click="handleDeleteClick"
								>
									<span v-if="isDeleteLoading" class="loader"></span>
									<template v-else>
										<AppIcon :name="IconName.TrashSmall" :size="14" :stroke-width="1.2" />
										{{ $t("opportunities.deleteButton") }}
									</template>
								</Button>
							</div>

							<a
								v-if="
									item?.parent_opportunity_type && store.isUserLoggedIn && store.currentUser.has(Permission.CrmRead)
								"
								class="opportunity-type"
								:href="`${HUBSPOT_URL}${opportunity?.hubspotParentDealId}`"
								target="_blank"
							>
								<opportunity-card-flag-icon
									:type="item.parent_opportunity_type"
									:salesProcessStatus="item.sales_process_status"
									:launchDateMs="item.launch_date_ms"
								/>

								<p>
									{{ $t("opportunities-layers." + item.parent_opportunity_type) }}
								</p>
							</a>
						</div>
					</AccordionItem>
				</AccordionRoot>
			</div>

			<div v-if="currentProject && item.type === 'opportunity_target' && showFullDetails" class="comment-section">
				<button
					v-if="commentCount > 0"
					class="comment-summary"
					@click="toggleComments"
					:class="{ expanded: isCommentsExpanded }"
				>
					<span class="summary-text">
						<b>{{ $t("opportunities.comments.comment_count", { count: commentCount }, commentCount) }}</b>
						<template v-if="latestCommentAuthor">
							· {{ $t("opportunities.comments.latest_by", { name: latestCommentAuthor }) }}
						</template>
					</span>
					<AppIcon :name="IconName.ChevronDown" class="chevron" :size="20" :stroke-width="1.6" />
				</button>

				<transition name="comment-expand">
					<div v-show="isCommentsExpanded" class="content">
						<div v-if="parsedComments.length > 0" class="comments-list">
							<div v-for="(comment, idx) in parsedComments" :key="idx" class="entry">
								<div v-if="comment.author" class="meta">
									<span class="username">{{ comment.author }}</span>
								</div>
								<div class="text">{{ comment.text }}</div>
							</div>
						</div>
					</div>
				</transition>

				<div class="box">
					<input
						type="text"
						:placeholder="$t('opportunities.comments.input_placeholder')"
						v-model="newComment"
						class="input"
						:disabled="isCommentLoading"
						@keydown.enter="handleCommentSubmit"
					/>
					<button
						class="submit"
						type="button"
						:aria-label="$t('opportunities.comments.add_comment_button')"
						@click="handleCommentSubmit"
						:disabled="isCommentLoading || !newComment.trim()"
					>
						<span v-if="isCommentLoading" class="loader"></span>
						<AppIcon v-else :name="IconName.Send" :size="16" :stroke-width="1.2" />
					</button>
				</div>
			</div>

			<div v-if="showFullDetails" class="accordions">
				<AccordionRoot :multiple="true" :default-value="[]" persistence-key="opportunity-card-v2-links-accordion">
					<AccordionItem value="links" class="links-accordion-item" :title="$t('opportunities.accordions.links')">
						<LinksIcons
							v-if="entityLinks.length > 0"
							:links="entityLinks"
							@select="id => captureOpportunityInteraction(id as OpportunityInteractionSimpleAction)"
						/>
					</AccordionItem>
				</AccordionRoot>
			</div>

			<div
				v-if="!showPrimaryBrokerCard && (!isHunting || showFullDetails)"
				class="details-section broker-section open-opportunity"
			>
				<div class="content" :class="{ 'with-collaborators': showCollaborativeBrokerList && !isHunting }">
					<div class="summary">
						<h4 class="open-opportunity-title">{{ $t("opportunities.openOpportunity.title") }}</h4>
						<p class="open-opportunity-text">{{ $t("opportunities.openOpportunity.description") }}</p>
					</div>
					<collaborative-broker-list
						v-if="showCollaborativeBrokerList && !isHunting"
						:brokers="collaborativeBrokers"
						:mobile="compact"
						@contact="captureOpportunityInteraction('contact')"
					/>
				</div>
			</div>

			<div
				v-if="(showPrimaryBrokerCard || showCollaborativeBrokerCards) && !isHunting"
				class="details-section broker-section"
			>
				<div class="content">
					<broker-card
						v-if="showPrimaryBrokerCard"
						variant="compact"
						:mobile="compact"
						:name="primaryBrokerName"
						:phone="item.broker_phone_number"
						:email="item.broker_email"
						:picture="item.broker_picture_url"
						:hubspotid="item.hubspot_record_id"
						:title="BROKER_TITLES[primaryBrokerName]"
						@contact="captureOpportunityInteraction('contact')"
					>
						<collaborative-broker-list
							v-if="showCollaborativeBrokerList && showFullDetails"
							:brokers="collaborativeBrokers"
							:mobile="compact"
							@contact="captureOpportunityInteraction('contact')"
						/>
					</broker-card>
					<div v-if="showCollaborativeBrokerCards && showFullDetails" class="collaborative-brokers">
						<broker-card
							v-for="broker in collaborativeBrokers"
							:key="broker.name"
							variant="compact"
							:mobile="compact"
							:name="broker.name"
							:email="broker.email"
							:phone="broker.phone"
							:picture="broker.picture"
							:title="broker.title"
							@contact="captureOpportunityInteraction('contact')"
						/>
					</div>
				</div>
			</div>

			<div v-if="hasAnyBrokerCard && isHunting && showFullDetails" class="accordions">
				<AccordionRoot :multiple="true" :default-value="[]" persistence-key="opportunity-card-v2-broker-accordion">
					<AccordionItem
						value="broker"
						class="broker-accordion-item"
						:title="
							$t(
								showCollaborativeBrokers
									? 'opportunities.accordions.brokersInformation'
									: 'opportunities.accordions.brokerInformation'
							)
						"
					>
						<div class="content broker-accordion-content">
							<broker-card
								v-if="showPrimaryBrokerCard"
								variant="compact"
								flush
								:mobile="compact"
								:name="primaryBrokerName"
								:phone="item.broker_phone_number"
								:email="item.broker_email"
								:picture="item.broker_picture_url"
								:hubspotid="item.hubspot_record_id"
								:title="BROKER_TITLES[primaryBrokerName]"
								@contact="captureOpportunityInteraction('contact')"
							>
								<collaborative-broker-list
									v-if="showCollaborativeBrokerList"
									:brokers="collaborativeBrokers"
									:mobile="compact"
									@contact="captureOpportunityInteraction('contact')"
								/>
							</broker-card>
							<collaborative-broker-list
								v-else-if="showCollaborativeBrokerList"
								:brokers="collaborativeBrokers"
								:mobile="compact"
								@contact="captureOpportunityInteraction('contact')"
							/>
							<div v-if="showCollaborativeBrokerCards" class="collaborative-brokers">
								<broker-card
									v-for="broker in collaborativeBrokers"
									:key="broker.name"
									variant="compact"
									flush
									:mobile="compact"
									:name="broker.name"
									:email="broker.email"
									:phone="broker.phone"
									:picture="broker.picture"
									:title="broker.title"
									@contact="captureOpportunityInteraction('contact')"
								/>
							</div>
						</div>
					</AccordionItem>
				</AccordionRoot>
			</div>

			<div v-if="showFullDetails" class="accordions">
				<AccordionRoot :multiple="true" :default-value="[]" persistence-key="opportunity-card-v2-accordion">
					<AccordionItem value="assetCategory" :title="t('opportunities.accordions.assetCategory')">
						<div class="content">
							<OpportunityEditFields
								v-if="isEditMode"
								:key="item.id"
								:opportunity="editableOpportunity"
								section="assetCategory"
								:disabled="isSavingOpportunity"
								@saving="isSavingOpportunity = $event"
							/>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.assetType") }}
								</span>
								<span class="value">
									{{ formatAssetType(item.assetType) }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.category") }}
								</span>
								<span class="value green">
									{{ formatLandCategory(item.landCategoryNew) }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.subcategory") }}
								</span>
								<span class="value">
									{{ formatLandSubcategory(item.landSubcategory) }}
								</span>
							</div>
						</div>
					</AccordionItem>
					<AccordionItem value="assetInformation" :title="t('opportunities.accordions.assetInformation')">
						<div class="content">
							<div class="detail parcels-detail">
								<span class="label">
									{{ $t("opportunities.lot_numbers_full") }}
								</span>
								<span v-if="dedupedLotNumbers.length > 0" class="value parcels">
									<span class="primary-parcel"
										>{{ dedupedLotNumbers[0]
										}}<template v-if="item.type === 'opportunity_target'">
											({{ $t("parcels.primary") }})</template
										></span
									>
									<span v-if="!isParcelsExpanded && dedupedLotNumbers.length > 1" class="parcels-count">
										+{{ dedupedLotNumbers.length - 1 }}
									</span>
									<span v-if="isParcelsExpanded && dedupedLotNumbers.length > 1" class="parcels-rest"
										>, {{ dedupedLotNumbers.slice(1).join(", ") }}</span
									>
									<button
										v-if="dedupedLotNumbers.length > 1"
										type="button"
										class="parcels-toggle"
										@click="isParcelsExpanded = !isParcelsExpanded"
									>
										{{ isParcelsExpanded ? $t("parcels.hide") : $t("parcels.showAll") }}
									</button>
								</span>
								<span v-else class="value">
									{{ $t("opportunities.unknown") }}
								</span>
							</div>
							<div class="detail">
								<span class="label">
									{{ $t("opportunities.all_owners_names") }}
								</span>
								<span v-if="item.all_owners_names && item.all_owners_names.length > 0" class="value">
									{{ formatField("all_owners_names") }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div class="detail">
								<span class="label">
									{{ $t("opportunities.primary_seller") }}
								</span>
								<span v-if="item.primary_seller" class="value">
									{{ formatField("primary_seller") }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div class="detail">
								<span class="label">
									{{ unitFieldLabel(OpportunityUnitField.LandArea) }}
								</span>
								<span class="value">
									{{ formatField("superficy") }}
								</span>
							</div>
							<div class="detail">
								<span class="label">
									{{ $t("opportunities.data_room_cloud_file") }}
								</span>
								<span class="value">
									<a
										v-if="item.data_room_cloud_file"
										class="link-pill"
										:href="item.data_room_cloud_file"
										target="_blank"
										rel="noopener noreferrer"
									>
										{{ $t("opportunities.link_cta") }}
										<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
									</a>
									<template v-else>
										{{ $t("opportunities.unknown") }}
									</template>
								</span>
							</div>
						</div>
					</AccordionItem>
					<AccordionItem value="pricing" :title="t('opportunities.accordions.pricing')">
						<div class="content">
							<OpportunityEditFields
								v-if="isEditMode"
								:key="item.id"
								:opportunity="editableOpportunity"
								section="pricing"
								:disabled="isSavingOpportunity"
								@saving="isSavingOpportunity = $event"
							/>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.pricingSource") }}
								</span>
								<span v-if="item.pricingSource" class="value">
									{{ formatField("pricingSource") }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ unitFieldLabel(OpportunityUnitField.BuildableArea) }}
								</span>
								<span class="value">
									{{ formatField("constructible") }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.residentialUnits") }}
								</span>
								<span class="value">
									{{ formatField("residentialUnits") }}
								</span>
							</div>
							<div class="detail">
								<span class="label">
									{{ unitFieldLabel(OpportunityUnitField.PricePerLandArea) }}
								</span>
								<span class="value">
									{{ formatField("pricePerSqft") }}
								</span>
							</div>
							<div class="detail">
								<span class="label">
									{{ unitFieldLabel(OpportunityUnitField.PricePerBuildableArea) }}
								</span>
								<span class="value">
									{{ formatField("pricePerConstructibleSqft") }}
								</span>
							</div>
							<div class="detail">
								<span class="label">
									{{ $t("opportunities.pricePerUnit") }}
								</span>
								<span class="value">
									{{ formatField("pricePerUnit") }}
								</span>
							</div>
						</div>
					</AccordionItem>
					<AccordionItem
						value="ownerInformation"
						v-if="item.type !== OpportunityType.ListingLanderz"
						:title="t('opportunities.accordions.ownerInformation')"
					>
						<div class="content">
							<OpportunityEditFields
								v-if="isEditMode"
								:key="item.id"
								:opportunity="editableOpportunity"
								section="ownerInformation"
								:disabled="isSavingOpportunity"
								@saving="isSavingOpportunity = $event"
							/>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.ownerTypes") }}
								</span>
								<span v-if="item.ownerTypes" class="value">
									{{ formatField("ownerTypes") }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.ownershipDuration") }}
								</span>
								<span v-if="item.ownershipDuration" class="value">
									{{ formatField("ownershipDuration") }}
								</span>
								<template v-else> {{ $t("opportunities.unknown") }} </template>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.motivationsToSell") }}
								</span>
								<span v-if="item.motivationsToSell" class="value">
									{{ formatField("motivationsToSell") }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.potentialDealStructures") }}
								</span>
								<span v-if="item.potentialDealStructures" class="value">
									{{ formatField("potentialDealStructures") }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div class="detail">
								<span class="label">
									{{ $t("opportunities.lastConversationOwner") }}
								</span>
								<span v-if="item.lastConversationOwner" class="value">
									{{ formatLastConversationDate(item.lastConversationOwner) }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.commentsAboutOwner") }}
								</span>
								<span v-if="item.commentsAboutOwner" class="value">
									{{ formatField("commentsAboutOwner") }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
						</div>
					</AccordionItem>
					<AccordionItem value="potentialProject" :title="t('opportunities.accordions.potentialProject')">
						<div class="content">
							<OpportunityEditFields
								v-if="isEditMode"
								:key="item.id"
								:opportunity="editableOpportunity"
								section="potentialProject"
								:disabled="isSavingOpportunity"
								@saving="isSavingOpportunity = $event"
							/>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.mainZoning") }}
								</span>
								<span class="value">
									{{ formatField("mainZoning") }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.projectApprovalProcesses") }}
								</span>
								<span class="value">
									{{ formatField("projectApprovalProcesses") }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.municipalReceptiveness") }}
								</span>
								<span class="value">
									{{ formatField("municipalReceptiveness") }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.modelling_link") }}
								</span>
								<span class="value">
									<span class="value">
										<a
											v-if="item.modelling_link"
											class="link-pill"
											:href="item.modelling_link"
											target="_blank"
											rel="noopener noreferrer"
										>
											{{ $t("opportunities.link_cta") }}
											<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
										</a>
										<template v-else>
											{{ $t("opportunities.unknown") }}
										</template>
									</span>
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.lastConversationCity") }}
								</span>
								<span v-if="item.lastConversationCity" class="value">
									{{ formatLastConversationDate(item.lastConversationCity, true) }}
								</span>
								<template v-else>
									{{ $t("opportunities.unknown") }}
								</template>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.information_on_potential_and_zoning") }}
								</span>
								<span class="value">
									{{ formatField("information_on_potential_and_zoning") }}
								</span>
							</div>
						</div>
					</AccordionItem>
					<AccordionItem value="existingConditions" :title="t('opportunities.accordions.existingConditions')">
						<div class="content">
							<OpportunityEditFields
								v-if="isEditMode"
								:key="item.id"
								:opportunity="editableOpportunity"
								section="existingConditions"
								:disabled="isSavingOpportunity"
								@saving="isSavingOpportunity = $event"
							/>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.knownDevelopmentRestrictions") }}
								</span>
								<span class="value">
									{{ formatField("knownDevelopmentRestrictions") }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.municipalInfrastructures") }}
								</span>
								<span class="value">
									{{ formatField("municipalInfrastructures") }}
								</span>
							</div>
							<div v-if="!isEditMode" class="detail">
								<span class="label">
									{{ $t("opportunities.information_on_existing_conditions") }}
								</span>
								<span class="value">
									{{ formatField("information_on_existing_conditions") }}
								</span>
							</div>
						</div>
					</AccordionItem>
					<AccordionItem value="disclaimer" :title="t('opportunities.accordions.disclaimer')">
						<div class="content">
							<div class="detail">
								<span class="value justified disclaimer">
									{{ $t("opportunities.disclaimer") }}
								</span>
							</div>
						</div>
					</AccordionItem>
				</AccordionRoot>
			</div>
		</div>

		<TransferProjectModal
			:is-open="isTransferModalOpen"
			:projects="transferToProjectOptions"
			:from-project-name="currentProject?.name"
			@close="isTransferModalOpen = false"
			@select="handleTransferSelect"
		/>

		<div ref="peekEndEl" aria-hidden="true" class="peek-end-marker"></div>
	</div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, watch } from "vue"
import { useElementSize } from "@vueuse/core"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { storeToRefs } from "pinia"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import BrokerCard from "@/components/opportunities/BrokerCard.vue"
import CollaborativeBrokerList, {
	type CollaborativeBroker,
} from "@/components/opportunities/CollaborativeBrokerList.vue"
import OpportunityCardFlagIcon from "@/components/opportunities/OpportunityCardFlagIcon.vue"
import Button from "@/components/ui/Button.vue"
import TransferProjectModal from "@/components/modals/TransferProjectModal.vue"
import LinksIcons, { type EntityLink } from "@/components/ui/LinksIcons.vue"
import { BROKER_TITLES, isRealBroker } from "@/utils/brokers"
import { IconBadge, resolveBadges } from "@/utils/opportunityIcons"
import { ProjectStoreKey, isHuntingMode } from "@/stores/projectStore.ts"
import { useOpportunityStore } from "@/stores/opportunityStore"
import { useToastStore } from "@/stores/toastStore.ts"
import { UseRootStore, Permission } from "@/model/RootStore.ts"
import { config, Feature } from "@/AppConfig"
import {
	getProjects,
	moveOpportunityTarget,
	deleteOpportunityTarget,
	createOpportunity,
	type ProjectListItem,
	type ProjectTypeFilter,
} from "@/utils/ApiClient"
import { useI18n } from "vue-i18n"
import { formatDateToISO, syncMinutesRemaining } from "@/utils/date"
import {
	analytics,
	type OpportunityInteractionChangeAction,
	type OpportunityInteractionSimpleAction,
} from "@/utils/analytics"
import { stageOrder, stageLabelMap } from "@/utils/opportunityStage"
import OpportunityEvaluation from "@/components/opportunities/OpportunityEvaluation.vue"
import {
	OpportunityPriority,
	OpportunityStage,
	OpportunityType,
	ProjectType,
	PricingSource,
	OwnerType,
	OwnershipDuration,
	MotivationToSell,
	PotentialDealStructure,
	MainZoning,
	ProjectApprovalProcess,
	MunicipalReceptiveness,
	KnownDevelopmentRestriction,
	MunicipalInfrastructure,
} from "@/graphql"
import type { Opportunity } from "@/model/DataModel"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { useTickingClock } from "@/composables/useTickingClock"
import { formatQuantity } from "@/utils/format"
import { isValidPrice } from "@/utils/opportunityPrice"
import {
	formatOpportunityUnitField,
	isOpportunityUnitField,
	OpportunityUnitField,
	opportunityUnitFieldLabel,
} from "@/utils/opportunityUnitFields"

import OpportunityEditFields from "./OpportunityEditFields.vue"

const HUBSPOT_URL = import.meta.env.VITE_HUBSPOT_DEAL_ENDPOINT

type SelectValueChange = {
	from: unknown
	to: unknown
}

const props = defineProps<{
	item: Record<string, any>
	mapRef?: any
	compact?: boolean
	// mobile sheet dragged past its peek snap point (driven by MobileDrawer)
	expanded?: boolean
}>()
const emit = defineEmits<{
	(e: "makeCcvSuccess"): void
	(e: "collapse"): void
	(e: "close"): void
	(e: "header-resize", height: number): void
}>()

// peeked, the compact sheet only shows the curated summary; expanding (or desktop) reveals everything else
const showFullDetails = computed(() => !props.compact || props.expanded)

// peekEndEl sits right after the last curated block; its offsetTop (unaffected by the drawer's
// clipping/transform) is the stacked height of everything shown while peeked -- reported so the
// mobile drawer can peek to exactly it (measurement wired up after compactAssetClassification below)
const headerEl = ref<HTMLElement | null>(null)
const peekEndEl = ref<HTMLElement | null>(null)

const isListingLink = computed(() => !!props.item.link && props.item.type === OpportunityType.ListingLanderz)

const { now: syncClockNow } = useTickingClock(15_000)
const syncStatusLabel = computed(() => (props.item.isPlaceholder ? t("opportunities.sync.syncing") : null))
const syncStatusSubtitle = computed(() => {
	if (!props.item.isPlaceholder) return null
	const launchDateMs = props.item.launch_date_ms
	if (!launchDateMs) return null
	const minutesRemaining = syncMinutesRemaining(launchDateMs, syncClockNow.value)
	return minutesRemaining > 0
		? t("opportunities.sync.minutesRemaining", { minutes: minutesRemaining })
		: t("opportunities.sync.longerThanExpected")
})

const showCreateTargetButton = computed(
	() =>
		currentProject.value != null &&
		props.item.type != null &&
		props.item.type !== "opportunity_target" &&
		config.ui.has(Feature.AddOpportunity) &&
		store.isUserLoggedIn &&
		store.currentUser.has(Permission.OpportunityTargetWrite)
)
const store = UseRootStore()
const { t } = useI18n()
const { areaUnit, areaUnitSymbol, pricePerAreaUnitSymbol } = useUnitSystem()
const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)
const opportunityStore = useOpportunityStore()
const isHunting = computed(() => isHuntingMode())
const isEditMode = ref(false)
const isSavingOpportunity = ref(false)
const editableOpportunity = computed(() => props.item as Opportunity)
const canEditOpportunity = computed(
	() =>
		!props.item.isPlaceholder &&
		props.item.canEdit &&
		(store.currentUser.has(Permission.OpportunityWrite) || store.currentUser.has(Permission.OpportunityAdmin))
)

watch(
	() => props.item.id,
	() => {
		isEditMode.value = false
		isSavingOpportunity.value = false
	}
)

watch(canEditOpportunity, canEdit => {
	if (!canEdit) {
		isEditMode.value = false
	}
})

function getOpportunityInteractionId(): number {
	return Number(props.item.id)
}

function captureOpportunityInteraction(action: OpportunityInteractionSimpleAction): void {
	analytics.opportunityInteracted({
		opportunityId: getOpportunityInteractionId(),
		action,
	})
}

function formatOpportunitySelectValue(action: OpportunityInteractionChangeAction, value: unknown): string {
	if (typeof value !== "string") {
		return ""
	}

	if (action === "pipelineStage") {
		return stageLabelMap[value as OpportunityStage] ?? value
	}

	return formatEnumValue(value)
}

function captureOpportunitySelectInteraction(
	action: OpportunityInteractionChangeAction,
	change: SelectValueChange
): void {
	analytics.opportunityInteracted({
		opportunityId: getOpportunityInteractionId(),
		action,
		from: formatOpportunitySelectValue(action, change.from),
		to: formatOpportunitySelectValue(action, change.to),
	})
}

const opportunity = computed(() => {
	return currentProject.value?.opportunityTargets.find(opp => opp.hubspotDealId === props.item.id) ?? null
})

const primaryBrokerName = computed(() => `${props.item.broker_first_name} ${props.item.broker_last_name}`)
const showPrimaryBrokerCard = computed(() => isRealBroker(primaryBrokerName.value))

const collaborativeBrokers = computed(() => {
	const slots = [
		[
			props.item.collaborative_broker_1_name,
			props.item.collaborative_broker_1_email,
			props.item.collaborative_broker_1_phone,
			props.item.collaborative_broker_1_picture,
		],
		[
			props.item.collaborative_broker_2_name,
			props.item.collaborative_broker_2_email,
			props.item.collaborative_broker_2_phone,
			props.item.collaborative_broker_2_picture,
		],
	]
	const seen = new Set([primaryBrokerName.value])
	return slots
		.filter(([name]) => {
			if (!isRealBroker(name) || seen.has(name)) return false
			seen.add(name)
			return true
		})
		.map(
			([name, email, phone, picture]): CollaborativeBroker => ({
				name: name!,
				email: email ?? undefined,
				phone: phone ?? undefined,
				picture: picture ?? undefined,
				title: BROKER_TITLES[name!],
			})
		)
})

const showCollaborativeBrokers = computed(() => collaborativeBrokers.value.length > 0)
const isLanderzListing = computed(() => props.item.type === OpportunityType.ListingLanderz)
const showCollaborativeBrokerCards = computed(() => showCollaborativeBrokers.value && isLanderzListing.value)
const showCollaborativeBrokerList = computed(() => showCollaborativeBrokers.value && !isLanderzListing.value)

const hasAnyBrokerCard = computed(() => showPrimaryBrokerCard.value || showCollaborativeBrokers.value)

const CARD_TAG_LABEL_KEYS: Partial<Record<IconBadge, string>> = {
	[IconBadge.New]: "filters.legend-new",
	[IconBadge.Offer]: "filters.legend-offer",
}

const cardTags = computed(() => {
	// unlike the map pin, this chip row doesn't suppress "New" for liked/disliked opportunities
	const badges = resolveBadges({
		launchDateMs: props.item.launch_date_ms,
		salesProcessStatus: props.item.sales_process_status,
	})
	return badges
		.filter((badge): badge is keyof typeof CARD_TAG_LABEL_KEYS => badge in CARD_TAG_LABEL_KEYS)
		.map(badge => ({ key: badge, label: t(CARD_TAG_LABEL_KEYS[badge]!) }))
})

const currencyDetailFields = new Set(["price", "priceExpectation", "pricePerUnit"])

interface ParsedComment {
	author: string | null
	text: string
}

const parsedComments = computed<ParsedComment[]>(() => {
	if (!opportunity.value?.comment) return []
	return opportunity.value.comment
		.split("\n")
		.filter(comment => comment.trim())
		.map(comment => {
			if (!comment.includes(":")) {
				return { author: null, text: comment }
			}
			const author = comment.split(":")[0].trim()
			const text = comment.substring(comment.indexOf(":") + 1).trim()
			return { author, text }
		})
})

const commentCount = computed(() => parsedComments.value.length)

const latestCommentAuthor = computed(() => {
	const comments = parsedComments.value
	if (comments.length === 0) return null
	return comments[comments.length - 1].author
})

const opportunitySelectFields: Record<string, { options: Record<string, string>; translationKey: string }> = {
	pricingSource: { options: PricingSource, translationKey: "pricingSource" },
	ownerTypes: { options: OwnerType, translationKey: "ownerType" },
	ownershipDuration: { options: OwnershipDuration, translationKey: "ownershipDuration" },
	motivationsToSell: { options: MotivationToSell, translationKey: "motivationToSell" },
	potentialDealStructures: { options: PotentialDealStructure, translationKey: "potentialDealStructure" },
	mainZoning: { options: MainZoning, translationKey: "mainZoning" },
	projectApprovalProcesses: { options: ProjectApprovalProcess, translationKey: "projectApprovalProcess" },
	municipalReceptiveness: { options: MunicipalReceptiveness, translationKey: "municipalReceptiveness" },
	knownDevelopmentRestrictions: { options: KnownDevelopmentRestriction, translationKey: "knownDevelopmentRestriction" },
	municipalInfrastructures: { options: MunicipalInfrastructure, translationKey: "municipalInfrastructure" },
}

function formatField(field: string): string {
	const value = props.item[field]

	if (
		value === null ||
		value === "null" ||
		value === undefined ||
		value === 0 ||
		value === Infinity ||
		Number.isNaN(value)
	) {
		return t("opportunities.unknown")
	}

	if (isOpportunityUnitField(field)) {
		return formatOpportunityUnitField(field, props.item as Opportunity, areaUnit.value)
	}

	if (currencyDetailFields.has(field)) {
		return `${formatQuantity(Math.round(value))}$`
	}

	if (field === "type") {
		return t(`opportunities-layers.${value}`)
	}

	const selectField = opportunitySelectFields[field]

	if (selectField) {
		const values = Array.isArray(value) ? value : [value]

		return values
			.map(option => {
				const key = Object.entries(selectField.options).find(([, enumValue]) => enumValue === option)?.[0]

				return key ? t(`property.${selectField.translationKey}.${key}`) : t("opportunities.unknown")
			})
			.join(", ")
	}

	if (Array.isArray(value)) return value.join(", ")
	return typeof value === "number" ? formatQuantity(value) : String(value)
}

function formatCurrency(field: string): string {
	const value = props.item[field]

	if (!isValidPrice(value)) {
		return t("opportunities.unknown")
	}

	return `$ ${formatQuantity(Number(value), { maximumFractionDigits: 0 })}`
}

function unitFieldLabel(field: OpportunityUnitField): string {
	return opportunityUnitFieldLabel(field, t, areaUnitSymbol.value, pricePerAreaUnitSymbol.value)
}

function areaFieldLabel(field: OpportunityUnitField.LandArea | OpportunityUnitField.BuildableArea): string {
	return t(`opportunities.areaFieldLabels.${field}`)
}

function formatAreaField(field: "superficy" | "constructible"): string {
	return formatField(field)
}

const newComment = ref("")
const isCommentLoading = ref(false)
const isCommentsExpanded = ref(false)
const isStageLoading = ref(false)
const isTransferLoading = ref(false)
const isPriorityLoading = ref(false)
const isMakeCcvLoading = ref(false)
const isDeleteLoading = ref(false)
const stageDropdown = ref<OpportunityStage | null>(null)
const isTransferModalOpen = ref(false)
const priorityDropdownValue = ref<OpportunityPriority | null>(null)
const allProjects = ref<ProjectListItem[]>([])
const toastStore = useToastStore()
onMounted(async () => {
	if (
		store.isUserLoggedIn &&
		store.currentUser.has(Permission.OpportunityTargetMove) &&
		config.ui.has(Feature.TransferOpportunityTarget) &&
		store.userApiToken
	) {
		try {
			allProjects.value = await getProjects(store.userApiToken, undefined)
		} catch {
			allProjects.value = []
		}
	}
})

const myWorkspaceProjectIds = computed(() => {
	const ids = new Set<string>()
	for (const p of store.userProjects) {
		if (p.type === "WORKSPACE") {
			ids.add(p.id)
		}
	}
	return ids
})

function isTransferDestinationAllowed(
	p: ProjectListItem,
	currentId: string | undefined,
	currentType: ProjectTypeFilter | undefined
): boolean {
	if (!currentId || p.id === currentId) {
		return false
	}
	if (currentType === "WORKSPACE") {
		return p.type !== "WORKSPACE"
	}
	if (currentType === "CCA") {
		if (p.type === "WORKSPACE") {
			return myWorkspaceProjectIds.value.has(p.id)
		}
		return p.type === "CCA"
	}
	if (currentType === "CCV") {
		if (p.type === "WORKSPACE") {
			return myWorkspaceProjectIds.value.has(p.id)
		}
		return p.type === "CCA" || p.type === "CCV"
	}
	return true
}

const opportunityStageOptions = computed(() =>
	stageOrder.map(stage => ({
		value: stage,
		label: stageLabelMap[stage],
	}))
)

const transferToProjectOptions = computed(() => {
	const projects = allProjects.value
	const currentId = currentProject.value?.id
	const currentType = currentProject.value?.type as ProjectTypeFilter | undefined
	const mine = myWorkspaceProjectIds.value
	const isPersonalWorkspace = (p: ProjectListItem) => p.type === "WORKSPACE" && mine.has(p.id)

	const createdAtMs = (p: ProjectListItem): number => {
		const t = Date.parse(p.createdAt)
		return Number.isFinite(t) ? t : 0
	}

	const filtered = projects.filter(p => isTransferDestinationAllowed(p, currentId, currentType))
	const ordered = [...filtered].sort((a, b) => {
		const rank = (p: ProjectListItem) => (isPersonalWorkspace(p) ? 0 : 1)
		const byGroup = rank(a) - rank(b)
		if (byGroup !== 0) return byGroup
		return createdAtMs(b) - createdAtMs(a)
	})
	return ordered.map(p => ({ value: p.id, label: p.name }))
})

const opportunityPriorityOptions = computed(() =>
	Object.values(OpportunityPriority).map(priority => ({
		value: priority,
		label: formatEnumValue(priority),
	}))
)
const canReadOpportunitySensitive = computed(() => store.currentUser.has(Permission.OpportunitySensitiveRead))
const canWriteOpportunitySensitive = computed(() => store.currentUser.has(Permission.OpportunitySensitiveWrite))

function findStageByLabel(label: string | undefined): OpportunityStage | null {
	if (!label) return null
	const entry = Object.entries(stageLabelMap).find(([, l]) => l === label)
	return (entry?.[0] as OpportunityStage | undefined) ?? null
}

watch(
	() => store.opportunityStage.get(Number(props.item.id)) ?? findStageByLabel(props.item.stageLabel),
	stage => {
		stageDropdown.value = stage
	},
	{ immediate: true }
)

watch(
	() => store.opportunityPriority.get(Number(props.item.id)) ?? props.item.priority ?? null,
	priority => {
		priorityDropdownValue.value = priority ?? null
	},
	{ immediate: true }
)

async function handleCommentSubmit(): Promise<void> {
	if (currentProject.value == null) {
		return
	}

	const commentValue = newComment.value.trim()

	if (!commentValue) {
		return
	}

	analytics.opportunityInteracted({
		opportunityId: getOpportunityInteractionId(),
		action: "addComment",
		comment: commentValue,
	})
	isCommentLoading.value = true

	try {
		if (opportunity.value == null) return
		await projectStore.addComment(opportunity.value.id, commentValue)
		newComment.value = ""
		// Always expand comments when a new comment is added even if the comment section is closed
		isCommentsExpanded.value = true
	} finally {
		isCommentLoading.value = false
	}
}

function toggleComments() {
	isCommentsExpanded.value = !isCommentsExpanded.value
}

async function handleStageChange() {
	const stage = stageDropdown.value
	const projectId = currentProject.value?.id

	if (!stage || !projectId || !canWriteOpportunitySensitive.value) {
		return
	}

	isStageLoading.value = true

	try {
		await store.updateOpportunity({
			opportunityId: String(props.item.id),
			projectId,
			stage,
		})
		store.opportunityStage.set(Number(props.item.id), stage)
		props.item.stageLabel = stageLabelMap[stage]
		stageDropdown.value = stage
		toastStore.success("opportunities.stageToast.success")
	} catch (err) {
		console.error("Failed to update opportunity stage:", err)
		toastStore.error("opportunities.stageToast.error")
	} finally {
		isStageLoading.value = false
	}
}

async function handleTransferSelect(toProjectId: string) {
	if (!toProjectId || !store.userApiToken) return
	const sourceProjectId = currentProject.value?.id

	const destination = transferToProjectOptions.value.find(project => project.value === toProjectId)?.label ?? ""
	analytics.opportunityInteracted({
		opportunityId: getOpportunityInteractionId(),
		action: "transfer",
		to: destination,
	})
	isTransferLoading.value = true
	try {
		await moveOpportunityTarget(store.userApiToken, {
			hubspotDealId: String(props.item.id),
			toProjectId,
			fromProjectId: sourceProjectId,
		})
		if (sourceProjectId) {
			opportunityStore.invalidateProjectOpportunities(sourceProjectId)
		}
		store.selectedOpportunityId = null
		toastStore.success("opportunities.transferToast.success")
	} catch (err) {
		console.error("Failed to move opportunity target:", err)
		toastStore.error("opportunities.transferToast.error")
	} finally {
		isTransferLoading.value = false
	}
}

async function handlePriorityChange() {
	if (currentProject.value == null || !canWriteOpportunitySensitive.value) {
		return
	}

	const priority = priorityDropdownValue.value ?? OpportunityPriority.Undetermined
	isPriorityLoading.value = true

	try {
		await store.updateOpportunity({
			opportunityId: String(props.item.id),
			projectId: currentProject.value.id,
			priority,
		})
		store.opportunityPriority.set(Number(props.item.id), priority)
		props.item.priority = priority
		priorityDropdownValue.value = priority
		toastStore.success("opportunities.priorityToast.success")
	} catch (err) {
		console.error("Failed to update opportunity priority:", err)
		toastStore.error("opportunities.priorityToast.error")
	} finally {
		isPriorityLoading.value = false
	}
}

async function handleDeleteClick() {
	if (!store.userApiToken || !currentProject.value?.id) {
		return
	}

	if (!window.confirm(t("opportunities.deleteConfirm"))) {
		return
	}

	captureOpportunityInteraction("deleteTarget")

	isDeleteLoading.value = true
	try {
		await deleteOpportunityTarget(store.userApiToken, Number(props.item.id), currentProject.value.id)
		opportunityStore.invalidateProjectOpportunities(currentProject.value.id)
		store.selectedOpportunityId = null
	} catch (err) {
		console.error("Failed to delete opportunity target:", err)
		toastStore.error("opportunities.deleteToast.error")
	} finally {
		isDeleteLoading.value = false
	}
}

async function handleMakeCcvClick() {
	if (!store.userApiToken || !currentProject.value?.id) return

	captureOpportunityInteraction("makeCcv")
	isMakeCcvLoading.value = true
	try {
		await moveOpportunityTarget(store.userApiToken, {
			hubspotDealId: String(props.item.id),
			fromProjectId: currentProject.value.id,
			convertToCcv: true,
		})
		opportunityStore.invalidateProjectOpportunities(currentProject.value.id)
		emit("makeCcvSuccess")
		store.selectedOpportunityId = null
	} catch (err: unknown) {
		console.error("Failed to convert target to CCV:", err)
		const msg = err instanceof Error ? err.message : ""
		if (msg.includes("still syncing")) {
			toastStore.error("opportunities.makeCcvToast.syncPending")
		} else if (msg.includes("required fields") || msg.includes("OPPORTUNITY_VALIDATION")) {
			toastStore.error("opportunities.makeCcvToast.validationError")
		} else {
			toastStore.error("opportunities.makeCcvToast.error")
		}
	} finally {
		isMakeCcvLoading.value = false
	}
}

function formatAddress(item: Record<string, any>): string {
	// only off_market_official's title shows a hubspot id instead of item.address, so only repeat it here for that type
	const shouldIncludeAddress = item.type === "off_market_official"

	return [shouldIncludeAddress && item.address, item.city, item.state, item.postal_code].filter(Boolean).join(", ")
}

function formatEnumValue(value: string): string {
	return value
		.split("_")
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ")
}

function formatAssetType(value: any): string {
	if (!value || (Array.isArray(value) && value.length === 0)) {
		return t("opportunities.unknown")
	}
	const values = Array.isArray(value) ? value : [value]
	return values.map((v: string) => t(`property.assetType.${v}`)).join(", ")
}

function formatLandCategory(value: any): string {
	if (!value || (Array.isArray(value) && value.length === 0)) {
		return t("opportunities.unknown")
	}
	const values = Array.isArray(value) ? value : [value]
	return values.map((v: string) => t(`property.landCategory.${v}`)).join(", ")
}

function formatLandSubcategory(value: any): string {
	if (!value || (Array.isArray(value) && value.length === 0)) {
		return t("opportunities.unknown")
	}
	const values = Array.isArray(value) ? value : [value]
	return values
		.map((v: string) => {
			const key = formatEnumValue(v).replace(/ /g, "")
			return t(`property.landSubcategory.${key}`)
		})
		.join(", ")
}

// condenses the assetCategory accordion's three fields into individual badges for the compact mobile card
const compactAssetClassification = computed(() => {
	const unknown = t("opportunities.unknown")
	return [
		formatAssetType(props.item.assetType),
		formatLandCategory(props.item.landCategoryNew),
		formatLandSubcategory(props.item.landSubcategory),
	].filter(part => part && part !== unknown)
})

const { height: headerHeight } = useElementSize(headerEl)
watch(
	[headerHeight, () => props.expanded, () => props.item, compactAssetClassification],
	async () => {
		if (props.expanded) return
		await nextTick()
		const peekHeight = peekEndEl.value?.offsetTop
		if (peekHeight && peekHeight > 0) emit("header-resize", peekHeight)
	},
	{ immediate: true, deep: true }
)

function formatLastConversationDate(value: unknown, dateOnly = false): string {
	if (!value) {
		return t("opportunities.unknown")
	}

	const dateValue = value instanceof Date ? value : new Date(value as string)

	if (Number.isNaN(dateValue.getTime())) {
		return t("opportunities.unknown")
	}

	return dateOnly ? dateValue.toISOString().slice(0, 10) : formatDateToISO(dateValue)
}

// this generic placeholder is returned as lot_image_url when a listing has no real photo
const PLACEHOLDER_IMAGE_URL = "https://storage.googleapis.com/landerz-app/listings/listing-joker.png"
const hasRealImage = computed(() => !!props.item.image && props.item.image !== PLACEHOLDER_IMAGE_URL)

const isHeroImageLoaded = ref(false)
watch(
	() => props.item.image,
	() => {
		isHeroImageLoaded.value = false
	}
)

const isParcelsExpanded = ref(false)
// lot_numbers_full can repeat the primary lot number; dedupe so the count/expanded list don't double-count it
const dedupedLotNumbers = computed<number[]>(() => Array.from(new Set(props.item.lot_numbers_full ?? [])))
const linkCopied = ref(false)
function copyLink() {
	captureOpportunityInteraction("copyLink")
	const url = new URL(window.location.href)
	url.searchParams.set("opp", props.item.hubspot_record_id)
	navigator.clipboard.writeText(url.toString())
	linkCopied.value = true
	setTimeout(() => {
		linkCopied.value = false
	}, 2000)
}

const entityLinks = computed<EntityLink[]>(() => {
	const canReadCrm = store.isUserLoggedIn && store.currentUser.has(Permission.CrmRead)

	if (props.item.type === "off_market_official") {
		if (!canReadCrm || !props.item.hubspot_record_id) {
			return []
		}

		return [
			{
				id: "hubspot",
				label: t("callToActions.openDeal"),
				icon: "hubspot",
				href: `${HUBSPOT_URL}${props.item.hubspot_record_id}`,
			},
		]
	}

	const links: EntityLink[] = [
		{
			id: "googleMaps",
			label: t("callToActions.openInMaps"),
			icon: "map-pin",
			href: `https://www.google.com/maps/search/?api=1&query=${props.item.latitude},${props.item.longitude}`,
		},
	]

	if (props.item.type === "listing_landerz" || props.item.type === "opportunity_target") {
		links.unshift({
			id: "copyLink",
			label: linkCopied.value ? t("callToActions.copied") : t("callToActions.copyLink"),
			icon: "link",
			onClick: copyLink,
		})
	}

	if (props.item.data_room_cloud_file) {
		links.push({
			id: "dataRoom",
			label: t("callToActions.dataRoom"),
			icon: "archive",
			href: props.item.data_room_cloud_file,
		})
	}

	if (props.item.modelling_link) {
		links.push({
			id: "modellingLink",
			label: t("opportunities.modelling_link"),
			icon: "giraffe",
			href: props.item.modelling_link,
		})
	}

	if (canReadCrm && props.item.hubspot_record_id) {
		links.push({
			id: "hubspot",
			label: t("callToActions.openDeal"),
			icon: "hubspot",
			href: `${HUBSPOT_URL}${props.item.hubspot_record_id}`,
		})
	}

	if (props.item.type === "opportunity_target" && canReadCrm && currentProject.value?.ccaContractHubspotId) {
		links.push({
			id: "contract",
			label: t("callToActions.openContract"),
			icon: "file-signature",
			href: `${HUBSPOT_URL}${currentProject.value?.ccaContractHubspotId}`,
		})
	}

	return links
})

const isQuickOpportunityLoading = ref(false)
async function handleQuickOpportunity(): Promise<void> {
	captureOpportunityInteraction("createTarget")

	try {
		isQuickOpportunityLoading.value = true

		const projectId = currentProject.value?.id
		if (!projectId) {
			toastStore.error("opportunity-creation.messages.error")
			return
		}
		const opportunityId = await createOpportunity(store.userApiToken, {
			parcelIDs: Array.from(props.item.lot_numbers_full).map((id: any) => id.toString()),
			totalAreaSqft: Number(props.item.analysis_land_area) || 0,
			marketingType: "internal",
			projectId,
			...(currentProject.value?.type !== "WORKSPACE"
				? { ccaContractHubspotId: currentProject.value?.ccaContractHubspotId }
				: {}),
			parentId: props.item.hubspot_record_id,
		})

		if (opportunityId == null) {
			toastStore.error("opportunity-creation.messages.error")
			isQuickOpportunityLoading.value = false
			return
		}

		props.mapRef?.registerPendingTargetSync?.(opportunityId)
		opportunityStore.invalidateProjectOpportunities(projectId, { selectOpportunityId: opportunityId })
		toastStore.success("opportunity-creation.messages.success")
	} catch (error) {
		console.error("Error creating opportunity:", error)
		toastStore.error("opportunity-creation.messages.error")
	} finally {
		props.item.priority = OpportunityPriority.Undetermined
		priorityDropdownValue.value = OpportunityPriority.Undetermined
		isQuickOpportunityLoading.value = false
	}
}
</script>

<style scoped>
.opportunity {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: var(--spacing-md);
	padding: var(--spacing-md) var(--spacing-lg);
	overflow: auto;
	background-color: var(--neutral-0);

	&.compact {
		justify-content: flex-start;
		padding-top: 0;
		border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;

		.stat-tiles {
			display: flex;
			flex-direction: column;

			.tile {
				flex-direction: row;
				align-items: center;
				justify-content: space-between;
			}
		}

		.broker-section {
			margin-top: var(--spacing-sm);
			padding-top: var(--spacing-md);
			border-top: var(--border-width-thin) solid var(--neutral-200);
		}

		:deep(.collaborative-broker-list .picture) {
			display: none;
		}

		:deep(.broker-card.compact) {
			.rows {
				display: none;
			}

			.top {
				padding: var(--spacing-xs) var(--spacing-sm);
			}

			.name {
				font-size: var(--font-size-small);
			}

			.title {
				font-size: var(--font-size-xxs);
			}
		}
	}

	.peek-end-marker {
		height: 0;
	}

	.items {
		display: contents;
	}

	&:not(.compact) {
		height: 100%;
		min-height: 0;
		justify-content: flex-start;
		overflow: hidden;

		.items {
			display: flex;
			flex: 1;
			min-height: 0;
			flex-direction: column;
			gap: var(--spacing-md);
			overflow-y: auto;
			scrollbar-gutter: stable;

			& > * {
				flex-shrink: 0;
			}
		}

		.header {
			position: relative;
			flex-shrink: 0;
			margin: calc(-1 * var(--spacing-md)) calc(-1 * var(--spacing-lg)) 0;
			padding: var(--spacing-md) var(--spacing-lg) var(--spacing-md);
			background-color: var(--neutral-0);

			&::after {
				content: "";
				position: absolute;
				left: var(--spacing-lg);
				right: var(--spacing-lg);
				bottom: 0;
				height: var(--border-width-thin);
				background-color: var(--neutral-400);
			}
		}

		& > .separator {
			display: none;
		}
	}

	.header {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		.price-block {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);
		}

		.price-label {
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}

		.price-value {
			display: block;
			color: var(--primary-green);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-2xl);
			font-weight: var(--font-weight-semibold);
			line-height: 1;
		}

		.header-top {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			gap: var(--spacing-sm);

			&:has(> .title) {
				align-items: center;
			}
		}

		.header-actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			flex-shrink: 0;
		}

		.title {
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-medium);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
		}

		.sub-title {
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-regular);
			line-height: var(--line-height-normal);
		}

		.badge-row {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: var(--spacing-xs);
			position: relative;
		}

		.badge {
			display: inline-flex;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background-color: var(--primary-green);
			color: var(--neutral-0);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);

			&.badge--opportunity_target {
				background-color: var(--accent-target);
			}

			&.badge--off_market_official {
				background-color: var(--accent-offmarket);
			}

			&.badge--listing_potential {
				background-color: rgb(158 223 188 / 25%);
				color: var(--primary-green);
			}
		}

		.chip {
			display: inline-flex;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background-color: var(--neutral-100);
			color: var(--neutral-700);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);

			&.chip--new {
				background-color: rgb(47 111 224 / 15%);
				color: var(--accent-new);
			}

			&.chip--offer {
				background-color: rgb(246 101 90 / 15%);
				color: var(--accent-offer);
			}
		}
	}

	.icon-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		padding: 0;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-full);
		background: var(--neutral-0);
		color: var(--neutral-600);
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			color: var(--primary-green);
			border-color: var(--primary-green);
		}

		&.active {
			color: var(--primary-green);
			border-color: var(--primary-green);
		}

		&.collapse-btn {
			width: auto;
			height: auto;
			padding: var(--spacing-sm);
			border: none;
			border-radius: var(--border-radius-sm);
			background: none;

			&:hover {
				background: var(--neutral-100);
				color: var(--neutral-900);
			}
		}
	}

	.separator {
		width: 100%;
		height: var(--border-width-thin);
		background-color: var(--neutral-400);
	}

	.stat-tiles {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--spacing-sm);

		.tile {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);
			padding: var(--spacing-sm) var(--spacing-md);
			border-radius: var(--border-radius-lg);
			background: var(--neutral-50);

			&:only-child {
				grid-column: 1 / -1;
				flex-direction: row;
				align-items: center;
				justify-content: space-between;
			}

			.label {
				color: var(--neutral-600);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-xxs);
				font-weight: var(--font-weight-semibold);
				text-transform: uppercase;
				letter-spacing: 0.02em;
			}

			.value {
				color: var(--neutral-900);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-medium);
				font-weight: var(--font-weight-semibold);
				line-height: var(--line-height-tight);

				.unit {
					font-size: var(--font-size-small);
				}
			}
		}
	}

	.opportunity-actions {
		display: flex;
		gap: var(--spacing-sm);

		.listing-btn,
		.create-target-btn {
			flex: 1 1 0;
			height: 36px;
			min-width: 0;
		}

		.listing-btn {
			flex-grow: 3;
		}

		.create-target-btn {
			flex-grow: 2;

			.loader {
				border-color: var(--neutral-700);
				border-bottom-color: transparent;
			}
		}
	}

	.compact-asset-badges {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xs);
	}

	.compact-asset-badge {
		display: inline-flex;
		align-items: center;
		padding: var(--spacing-xxs) var(--spacing-sm);
		border-radius: var(--border-radius-lg);
		background-color: var(--neutral-600);
		color: var(--neutral-50);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.hero-image {
		position: relative;
		height: var(--height-opportunity-image);

		.overview-image {
			display: block;
			width: 100%;
			height: 100%;
			border-radius: var(--border-radius-lg);
			object-fit: cover;
			object-position: center;
			opacity: 0;
			transition: opacity 0.15s ease;

			&.loaded {
				opacity: 1;
			}
		}

		.image-skeleton {
			position: absolute;
			inset: 0;
			border-radius: var(--border-radius-lg);
			background: linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-50) 37%, var(--neutral-100) 63%);
			background-size: 400% 100%;
			animation: image-skeleton-shimmer 1.4s ease infinite;
		}
	}

	@keyframes image-skeleton-shimmer {
		0% {
			background-position: 100% 0;
		}

		100% {
			background-position: 0 0;
		}
	}

	.detail-actions {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
		margin-left: auto;

		.toggle-edit-btn {
			min-width: 0;
			width: fit-content;
		}
	}

	.details-section {
		padding: 0;
		background-color: var(--neutral-0);
	}

	.accordions {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		:deep(.accordion-content) {
			padding: var(--spacing-xs) var(--spacing-sm);
		}
	}

	:deep(.broker-accordion-item) {
		.accordion-content {
			padding: 0;
		}
	}

	:deep(.links-accordion-item) {
		background: var(--neutral-50);

		.accordion-trigger {
			background: var(--neutral-50);
			min-height: unset;
			padding-top: var(--spacing-xs);
			padding-bottom: var(--spacing-xs);
		}

		.accordion-content {
			background: var(--neutral-0);
			padding-top: var(--spacing-sm);
			padding-bottom: var(--spacing-sm);
		}

		.accordion-legend {
			color: var(--neutral-700);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-medium);
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}
	}

	:deep(.target-tracking-item) {
		background: var(--neutral-50);

		.accordion-trigger {
			background: var(--neutral-50);
		}

		.accordion-content {
			background: var(--neutral-0);
		}

		.accordion-legend {
			color: var(--neutral-700);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-medium);
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}
	}

	.target-tracking-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		.opportunity-type {
			display: flex;
			gap: var(--spacing-sm);
			align-items: center;

			img {
				width: var(--grid-column-sm);
			}

			p {
				color: var(--neutral-900);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-base);
				font-weight: var(--font-weight-regular);
				line-height: var(--line-height-normal);
			}

			&:empty {
				display: none;
			}
		}

		.target-actions {
			display: flex;
			gap: var(--spacing-sm);

			.ccv-btn,
			.transfer-btn {
				flex: 1 1 0;
				min-width: 0;
			}

			.transfer-btn {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				gap: var(--spacing-xs);
			}
		}

		.target-dropdown {
			margin-top: 0;
		}

		:deep(.ark-listbox-dropdown .label) {
			font-size: var(--font-size-xs);
		}
	}

	.collaborative-brokers {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
	}

	.details-section.broker-section .content {
		gap: var(--spacing-sm);
	}

	.broker-section.open-opportunity .content {
		gap: 0;
		border: var(--border-width-thin) solid var(--neutral-100);
		border-radius: var(--border-radius-lg);
		background: color-mix(in srgb, var(--neutral-50) 50%, transparent);

		&.with-collaborators {
			border-color: var(--neutral-200);
		}

		.summary {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);
			padding: var(--spacing-sm) var(--spacing-md);
		}

		.collaborative-broker-list {
			--inner-radius: calc(var(--border-radius-lg) - var(--border-width-thin));
			border-radius: 0 0 var(--inner-radius) var(--inner-radius);
		}

		.open-opportunity-title {
			margin: 0;
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
		}

		.open-opportunity-text {
			margin: 0;
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
		}
	}

	.broker-accordion-content {
		display: flex;
		flex-direction: column;

		.collaborative-brokers {
			gap: 0;
			margin-top: 0;
		}
	}

	.details-section {
		.content {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			gap: var(--spacing-xs);
		}
	}

	.accordions .content {
		display: flex;
		flex-direction: column;
		justify-content: space-between;

		.detail {
			display: flex;
			flex-wrap: wrap;
			gap: var(--spacing-xxs) var(--spacing-sm);
			padding: 6px 0;
			justify-content: space-between;
			align-items: baseline;
			border-bottom: var(--border-width-thin) solid var(--neutral-200);

			&:last-child {
				border-bottom: none;
			}

			.label {
				color: var(--neutral-600);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				font-weight: var(--font-weight-regular);
				line-height: var(--line-height-normal);
				flex: 1 1 var(--min-width-field);
			}

			.value {
				color: var(--neutral-900);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				font-weight: var(--font-weight-regular);
				line-height: var(--line-height-normal);

				.unit {
					color: var(--neutral-400);
				}

				&.green {
					color: var(--primary-green);
				}

				&.justified {
					text-align: justify;
				}

				&.disclaimer {
					font-size: var(--font-size-xxs);
				}

				.parcels-count {
					font-size: var(--font-size-xxs);
					color: var(--neutral-600);
				}

				.link-pill {
					display: inline-flex;
					align-items: center;
					gap: var(--spacing-xxs);
					padding: var(--spacing-xxs) var(--spacing-sm);
					border-radius: var(--border-radius-full);
					background-color: var(--neutral-100);
					color: var(--neutral-700);
					font-size: var(--font-size-xs);
					font-weight: var(--font-weight-medium);
					text-decoration: none;
					transition:
						background-color 0.2s ease,
						color 0.2s ease;

					&:hover {
						background-color: var(--neutral-200);
						color: var(--neutral-900);
					}
				}
			}

			&.parcels-detail {
				display: flex;
				flex-wrap: nowrap;
				align-items: baseline;

				.label {
					flex: 0 0 auto;
				}

				.value.parcels {
					flex: 1 1 auto;
					min-width: 0;
					text-align: right;
				}

				.parcels-toggle {
					display: block;
					width: 100%;
					margin-top: var(--spacing-xxs);
					text-align: right;
					background: none;
					border: none;
					padding: 0;
					font: inherit;
					font-size: var(--font-size-xxs);
					font-weight: var(--font-weight-semibold);
					color: var(--neutral-600);
					cursor: pointer;

					&:hover {
						color: var(--primary-green);
					}
				}
			}
		}
	}

	.comment-section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		background: var(--neutral-50);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
		padding: var(--spacing-sm) var(--spacing-md);

		.comment-summary {
			background: none;
			border: none;
			padding: 0;
			font: inherit;
			color: inherit;
			cursor: pointer;
			outline: inherit;
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			width: 100%;
			text-align: left;

			.summary-text {
				flex: 1;
				min-width: 0;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				color: var(--neutral-600);

				b {
					font-weight: var(--font-weight-medium);
					color: var(--neutral-900);
				}
			}

			.chevron {
				width: var(--size-icon-md);
				height: var(--size-icon-md);
				color: var(--neutral-600);
				flex-shrink: 0;
				transition: transform 0.2s ease;
			}

			&:hover {
				.summary-text,
				.chevron {
					color: var(--primary-green);
				}
			}

			&.expanded .chevron {
				transform: rotate(180deg);
			}
		}

		.content {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-sm);
			overflow: hidden;
		}

		.box {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);

			.input {
				flex: 1;
				padding: 0 var(--spacing-sm) 0 var(--space-10);
				border: var(--border-width-thin) solid var(--neutral-200);
				border-radius: var(--border-radius-base);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				background: var(--neutral-0);
				transition: border-color 0.2s ease;
				height: var(--space-30);

				&:focus {
					outline: none;
					border-color: var(--primary-green);
				}
			}

			.submit {
				display: flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				width: var(--space-30);
				height: var(--space-30);
				background: var(--primary-green);
				color: var(--neutral-0);
				border: none;
				border-radius: var(--border-radius-base);
				cursor: pointer;
				transition: background-color 0.2s ease;

				&:hover:not(:disabled) {
					background: var(--brand-green);
				}

				&:disabled {
					opacity: 0.5;
					cursor: not-allowed;
				}
			}
		}
	}

	.delete-target {
		align-self: center;

		.delete-btn {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-xs);
			color: var(--neutral-600);

			&:hover:not(.btn--disabled) {
				color: var(--accent-warning);
				text-decoration: none;
			}
		}
	}

	.target-dropdown {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		margin-top: var(--spacing-sm);

		.submit {
			align-self: flex-start;
			background: var(--primary-green);
			color: var(--neutral-0);
			border: none;
			border-radius: var(--border-radius-md);
			padding: var(--spacing-xs) var(--spacing-md);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-base);
			font-weight: var(--font-weight-semibold);
			cursor: pointer;
			transition: background-color 0.2s ease;
			height: var(--spacing-xl);

			&:hover:not(:disabled) {
				background: var(--brand-green);
			}

			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}
	}

	.comments-list {
		display: flex;
		flex-direction: column;

		.entry {
			padding: var(--spacing-xs) 0;
			font-family: var(--font-family-primary);

			& + .entry {
				border-top: var(--border-width-thin) solid var(--neutral-100);
			}

			.meta {
				margin-bottom: var(--spacing-xxs);
			}

			.username {
				font-size: var(--font-size-small);
				font-weight: var(--font-weight-medium);
				color: var(--neutral-900);
			}

			.text {
				font-size: var(--font-size-small);
				line-height: var(--line-height-normal);
				color: var(--neutral-700);
				word-break: break-word;
			}
		}
	}

	.loader {
		width: var(--size-icon-lg);
		height: var(--size-icon-lg);
		border: var(--border-width-base) solid var(--neutral-0);
		border-bottom-color: transparent;
		border-radius: var(--border-radius-full);
		display: inline-block;
		animation: rotation 1s linear infinite;
	}

	.comment-expand-enter-active,
	.comment-expand-leave-active {
		transition: all 0.3s ease-in-out;
		max-height: var(--max-height-animation);
		opacity: 1;
	}

	.comment-expand-enter-from,
	.comment-expand-leave-to {
		max-height: 0;
		opacity: 0;
		padding-top: 0;
		padding-bottom: 0;
		margin-top: 0;
		margin-bottom: 0;
	}

	.comment-expand-enter-to,
	.comment-expand-leave-from {
		max-height: var(--max-height-animation);
		opacity: 1;
	}

	@keyframes rotation {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
}

@media (max-width: 600px) {
	.opportunity {
		.header .title {
			font-size: var(--font-size-large);
		}
	}

	.opportunity :deep(.broker-card:not(.compact) .avatar) {
		width: 80px;
		height: 80px;
	}
}

/* short viewports: tighten compact-mode spacing/type so the uncapped peeked sheet still fits without scrolling */
@media (max-height: 700px) {
	.opportunity.compact {
		gap: var(--spacing-sm);

		.header {
			gap: var(--spacing-xxs);
		}

		.stat-tiles .tile {
			padding: var(--spacing-xs) var(--spacing-md);

			.value {
				font-size: var(--font-size-base);
			}
		}

		:deep(.broker-card) {
			.avatar {
				width: 40px;
				height: 40px;
			}
		}
	}
}
</style>
